import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

import { 
  Save, ArrowLeft, Warehouse, CreditCard, 
  ShieldCheck, Search, List, Package, Tag, Info, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, Type, Minus, Plus, Paperclip
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal'; // 🎯 المرفقات
import { usePermissions } from './usePermissions'; // 🎯 الصلاحيات
import { useFontSize } from './FontSizeContext'; // 🎯 التحكم في الخط

const StoreroomDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  
  const { canCreate, canUpdate, canDelete } = usePermissions('storerooms');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  const [activeTab, setActiveTab] = useState('MAIN'); 
  const [inventory, setInventory] = useState([]); 
  const [loadingInv, setLoadingInv] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  const [data, setData] = useState({
    location: '', description: '', siteid: sessionSite, orgid: sessionOrg,
    isdefault: false, useinpo: true, invowner: '',
    glaccount: '', controlacc: '', shrinkacc: ''
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {} });

  // --- مكون التحكم في الخط ---
  const FontControls = () => (
    <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
      <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
      <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
        <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
      </div>
      <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
    </div>
  );

  // 1. جلب البيانات الأساسية
  useEffect(() => {
    if (!isNew) {
      axios.get(`/api/storerooms/${id}`)
        .then(res => setData(res.data))
        .catch(() => toast.error(t('common.load_error', "Error fetching storeroom details")));
    }
  }, [id, isNew, t]);

  // 2. جلب المرفقات
  const fetchCount = useCallback(async () => {
    if (!id || isNew) return;
    try {
      const res = await axios.get(`/api/attachments/count/STOREROOM/${id}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Attachment err:", err); }
  }, [id, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  // 3. جلب النافيجيشن بالكويري
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token'); 
      const res = await axios.get(`/api/storerooms/${id}/navigation${location.search}`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { console.error("Nav failed", err); }
  }, [id, isNew, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const goTo = (targetId) => targetId && navigate(`/storerooms/${targetId}${location.search}`);

  // 4. جلب الأصناف عند فتح تابة Inventory
  useEffect(() => {
    if (activeTab === 'INVENTORY' && !isNew) {
        setLoadingInv(true);
        axios.get(`/api/storerooms/${id}/inventory`)
          .then(res => setInventory(res.data))
          .catch(() => setInventory([]))
          .finally(() => setLoadingInv(false));
    }
  }, [activeTab, id, isNew]);

  const handleInputChange = (field, value) => {
    setData(prev => {
        if (field === 'orgid') return { ...prev, [field]: value, siteid: '' };
        return { ...prev, [field]: value };
    });
  };

  const handleSmartSearch = (fieldName, title, defaultType) => {
    let extraFilters = {};
    if (fieldName === 'siteid' && data.orgid) {
        extraFilters = { org_id: data.orgid };
    }
    setLookup({ isOpen: true, title, type: defaultType, field: fieldName, filters: extraFilters });
  };

  const handleSave = async () => {
    if (!data.location || data.location.trim() === "") return toast.error(t('storerooms.messages.req_fields', "Location ID is required!"));
    if (!data.orgid || !data.siteid) return toast.error(t('storerooms.messages.req_org_site', "Org and Site are required!"));

    const loadId = toast.loading(t('common.saving', "Saving Storeroom..."));
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/storerooms/save', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(t('common.save_success', "Saved Successfully"), { id: loadId });
      if (isNew) navigate(`/storerooms/${data.location}${location.search}`);
    } catch (err) {
      toast.error(t('common.save_fail', "Save failed"), { id: loadId });
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('storerooms.messages.delete_warn', { store: data.location }, `You are about to delete Storeroom ${data.location}. This action cannot be undone!`),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (result.isConfirmed) {
      const loadId = toast.loading(t('common.deleting', "Deleting storeroom..."));
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/storerooms/${data.location}`, { headers: { 'Authorization': `Bearer ${token}` } });
        toast.success(t('common.delete_success', "Storeroom deleted successfully"), { id: loadId });
        navigate(`/storerooms${location.search}`);
      } catch (err) {
        toast.error(t('common.delete_fail', "Delete failed"), { id: loadId });
      }
    }
  };

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] uppercase tracking-tighter duration-300 transition-[font-size] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
            const val = row.site_id || row.org_id || row.comp_num || row.value || row.id;
            handleInputChange(lookup.field, val);
            setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />

      {isDocModalOpen && !isNew && (
        <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="STOREROOM" ownerid={id} />
      )}

      {/* --- TOP TOOLBAR --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/storerooms${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>
          
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}

          <div className="flex flex-col ml-4">
              <span className="text-[0.8em] text-orange-400 font-bold uppercase tracking-widest">{t('storerooms.details.profile', 'Storeroom')}</span>
              <h1 className="text-sm font-black tracking-tighter">{data.location || t('common.new', 'NEW RECORD')}</h1>
          </div>
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
            <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all uppercase">
              <Save size={14}/> {t('common.save', 'SAVE CHANGES')}
            </button>
          )}

          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-red-500/20 text-red-400 rounded transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}

          {!isNew && (
            <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.attachments', 'Attachments')}>
              <Paperclip size={16} />
              {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
            </button>
          )}

          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          <FontControls />
        </div>
      </div>

      {/* --- HEADER INFO --- */}
      <div className="bg-white border-b px-8 py-4 flex items-center gap-12 shadow-sm z-20">
        <div className="flex flex-col min-w-[200px]">
          <span className="text-[0.8em] text-gray-400 font-black uppercase tracking-widest">{t('storerooms.fields.location', 'Storeroom')}</span>
          <input 
              className={`bg-transparent outline-none font-black text-[#1a3353] text-xl w-32 border-b-2 border-transparent focus:border-orange-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}
              value={data.location}
              disabled={!isNew}
              onChange={(e) => handleInputChange('location', e.target.value.toUpperCase())}
              placeholder={t('common.new', 'NEW')}
          />
        </div>
        
        <div className={`flex flex-col flex-1 border-gray-200 px-8 ${isRTL ? 'border-r' : 'border-l'}`}>
          <label className="text-[0.8em] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.desc', 'Description')}</label>
          <span className="text-sm font-bold text-gray-700 mt-1 truncate">{data.description || t('storerooms.details.record', 'Storeroom Record')}</span>
        </div>


      </div>

      {/* --- TABS --- */}
      <div className="flex bg-[#1a3353] text-white shadow-md px-4 overflow-x-auto no-scrollbar">
        {[
          { id: 'MAIN', label: t('storerooms.tabs.main', 'MAIN'), icon: <List size={14}/> },
          { id: 'INVENTORY', label: t('storerooms.tabs.inventory', 'INVENTORY'), icon: <Package size={14}/> }
        ].map(tb => (
          <button key={tb.id} onClick={() => setActiveTab(tb.id)} 
            className={`px-8 py-3 flex items-center gap-2 font-black border-white/5 transition-all whitespace-nowrap ${isRTL ? 'border-l' : 'border-r'} ${activeTab === tb.id ? 'bg-white text-[#1a3353]' : 'hover:bg-white/10 opacity-75'}`}>
            {tb.icon} {tb.label}
          </button>
        ))}
      </div>

      {/* --- Main Content --- */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-[#f8f9fb]">
        {activeTab === 'MAIN' ? (
            <div className="bg-white p-10 border shadow-sm max-w-5xl mx-auto space-y-10 animate-in fade-in duration-500 rounded-sm border-t-4 border-t-blue-600">
                <div className="grid grid-cols-2 gap-20">
                    <div className="space-y-6">
                        <h3 className={`text-[0.9em] font-black text-gray-400 border-b pb-2 tracking-[0.2em] flex items-center gap-2 `}>
                            <Info size={14} className="text-blue-600"/> {t('storerooms.details.gen_settings', 'General Settings')}
                        </h3>
                        <EditField label={t('assets.headers.desc', 'Description')} value={data.description} onChange={v => handleInputChange('description', v)} isRTL={isRTL} />
                        <EditField label={t('common.org', 'Organization')} disabled={true} value={data.orgid}  isRTL={isRTL} />
                        <EditField label={t('common.site', 'Site')} disabled={true} value={data.siteid}  isRTL={isRTL} />
                        <EditLookupField label={t('storerooms.fields.invowner', 'Inventory Owner')} value={data.invowner} onSearch={() => setLookup({isOpen:true, title: t('storerooms.lookup.person', 'Select Person'), type:'persons', field:'invowner'})} isRTL={isRTL} />
                        
                        <div className={`flex gap-10 pt-4 `}>
                            <label className={`flex items-center gap-3 cursor-pointer group `}>
                                <input type="checkbox" checked={data.isdefault} onChange={e => handleInputChange('isdefault', e.target.checked)} className="w-4 h-4 accent-blue-600" />
                                <span className="font-bold text-gray-500 group-hover:text-blue-600 transition-colors uppercase">{t('storerooms.fields.isdefault', 'Default Storeroom?')}</span>
                            </label>
                            <label className={`flex items-center gap-3 cursor-pointer group `}>
                                <input type="checkbox" checked={data.useinpo} onChange={e => handleInputChange('useinpo', e.target.checked)} className="w-4 h-4 accent-blue-600" />
                                <span className="font-bold text-gray-500 group-hover:text-blue-600 transition-colors uppercase">{t('storerooms.fields.useinpo', 'Use in PO?')}</span>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h3 className={`text-[0.9em] font-black text-gray-400 border-b pb-2 tracking-[0.2em] flex items-center gap-2 `}>
                            <CreditCard size={14} className="text-green-600"/> {t('storerooms.details.gl_accounts', 'GL Accounts')}
                        </h3>
                        <EditField label={t('storerooms.fields.controlacc', 'Control Account')} value={data.controlacc} onChange={v => handleInputChange('controlacc', v)} isRTL={isRTL} />
                        <EditField label={t('storerooms.fields.shrinkacc', 'Shrinkage Account')} value={data.shrinkacc} onChange={v => handleInputChange('shrinkacc', v)} isRTL={isRTL} />
                        <EditField label={t('storerooms.fields.glaccount', 'Default GL Account')} value={data.glaccount} onChange={v => handleInputChange('glaccount', v)} isRTL={isRTL} />

                        <div className={`bg-blue-50/50 p-6 rounded mt-12 shadow-inner ${isRTL ? 'border-r-4 border-r-[#1a3353] text-right' : 'border-l-4 border-l-[#1a3353] text-left'}`}>
                            <div className={`flex items-center gap-2 text-[#1a3353] font-black mb-1 uppercase tracking-widest text-[0.8em] `}>
                                <ShieldCheck size={16}/> <span>{t('storerooms.details.inv_control', 'Inventory Control')}</span>
                            </div>
                            <p className="text-gray-500 italic text-[0.9em] leading-relaxed lowercase">
                                {t('storerooms.details.inv_control_desc', 'all material issues and receipts for this storeroom will be balanced against these accounts.')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            /* --- Inventory Tab --- */
            <div className="bg-white border shadow-2xl max-w-6xl mx-auto rounded-sm overflow-hidden animate-in slide-in-from-right duration-300 border-t-4 border-t-blue-800">
                <div className={`bg-gray-50 p-4 px-8 border-b flex justify-between items-center `}>
                    <div className={`flex items-center gap-3 `}>
                        <div className="p-2 bg-blue-600 rounded text-white shadow-md"><Package size={16}/></div>
                        <div>
                            <span className="font-black tracking-widest text-[#1a3353] uppercase">{t('storerooms.tabs.inventory', 'Current Inventory Balances')}</span>
                            <p className="text-[0.8em] text-gray-400 font-bold mt-0.5 uppercase tracking-tighter">{t('storerooms.fields.location', 'Location')}: {data.location}</p>
                        </div>
                    </div>
                    <div className={`flex items-center gap-4 `}>
                        <div className={isRTL ? 'text-left' : 'text-right'}>
                            <span className="text-[0.8em] font-black text-gray-400 block uppercase italic">{t('storerooms.details.items_stocked', 'Items Stocked')}</span>
                            <span className="text-lg font-black text-blue-900 leading-none">{inventory.length}</span>
                        </div>
                        <button onClick={() => toast.success(t('common.refresh', "Refreshed"))} className="p-2 text-gray-400 hover:text-blue-600 transition-all"><RefreshCw size={16}/></button>
                    </div>
                </div>
                
                <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-[#f9fafb] border-b text-gray-400 font-black uppercase text-[0.8em] tracking-tighter">
                        <tr>
                            <th className={`p-4 border-gray-100 w-32 ${isRTL ? 'border-l' : 'border-r'}`}>{t('storerooms.tables.itemnum', 'Item Num')}</th>
                            <th className={`p-4 border-gray-100 ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc', 'Description')}</th>
                            <th className={`p-4 border-gray-100 w-20 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('storerooms.tables.uom', 'UOM')}</th>
                            <th className={`p-4 border-gray-100 w-32 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('storerooms.tables.curbal', 'Current Bal')}</th>
                            <th className={`p-4 border-gray-100 w-32 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('storerooms.tables.reserved', 'Reserved Qty')}</th>
                            <th className="p-4 w-28 text-center uppercase">{t('assets.headers.status', 'Status')}</th>
                        </tr>
                    </thead>
                    <tbody className="text-[0.9em] font-bold">
                        {loadingInv ? (
                            <tr><td colSpan="6" className="p-20 text-center animate-pulse text-gray-400 uppercase font-black tracking-[0.2em]">{t('common.syncing', 'Syncing...')}</td></tr>
                        ) : inventory.length === 0 ? (
                            <tr><td colSpan="6" className="p-20 text-center text-gray-300 italic font-black uppercase tracking-widest">{t('common.no_records', 'No records found')}</td></tr>
                        ) : inventory.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-blue-50/50 transition-colors group uppercase">
                                <td className={`p-4 border-gray-50 font-black text-blue-900 uppercase group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>
                                    <Tag size={10} className={`inline text-blue-300 ${isRTL ? 'ml-2' : 'mr-2'}`}/> {item.itemnum}
                                </td>
                                <td className={`p-4 border-gray-50 text-gray-600 ${isRTL ? 'border-l' : 'border-r'}`}>{item.description}</td>
                                <td className={`p-4 border-gray-50 text-center text-gray-400 font-black ${isRTL ? 'border-l' : 'border-r'}`}>{item.orderunit || 'EA'}</td>
                                <td className={`p-4 border-gray-50 text-green-700 font-mono text-[1.1em] ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>
                                    {parseFloat(item.curbal || 0).toFixed(2)}
                                </td>
                                <td className={`p-4 border-gray-50 text-orange-600 font-mono ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>
                                    {parseFloat(item.reserved || 0).toFixed(2)}
                                </td>
                                <td className="p-4 text-center">
                                    <span className={`px-3 py-1 rounded-sm text-[0.8em] font-black uppercase shadow-sm ${item.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                      {item.status || 'ACTIVE'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
      </div>
    </div>
  );
};

// --- Reusable Components ---

const EditField = ({ label, value, onChange, isRTL }) => (
  <div className={`flex flex-col border-b border-gray-200 focus-within:border-blue-500 transition-all group ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-tighter group-focus-within:text-blue-500">{label}</label>
    <input 
        className={`font-bold text-blue-900 outline-none py-1.5 bg-transparent text-[0.9em] uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`} 
        value={value || ''} 
        onChange={(e)=>onChange(e.target.value)} 
    />
  </div>
);

const EditLookupField = ({ label, value, onSearch, desc, isRTL }) => (
  <div className={`flex flex-col border-b border-gray-200 group focus-within:border-blue-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-tighter group-focus-within:text-blue-500">{label}</label>
    <div className={`flex items-center justify-between py-1 `}>
      <div className={`flex items-center gap-2 `}>
        <input 
          className={`font-black text-blue-900 outline-none bg-transparent flex-1 uppercase text-[0.9em] ${!value ? 'italic font-normal text-gray-300' : ''} ${isRTL ? 'text-right' : 'text-left'}`} 
          value={value || 'Select...'} 
          readOnly 
        />
        {desc && <span className="text-[0.8em] text-gray-300 italic whitespace-nowrap lowercase">({desc})</span>}
      </div>
      <div className="p-1 hover:bg-blue-50 rounded-full cursor-pointer transition-colors" onClick={onSearch}>
        <Search size={14} className="text-blue-600" />
      </div>
    </div>
  </div>
);

export default StoreroomDetails;